import { useState, useEffect, useRef } from 'react';
import { Mail, Eye, Send, ChevronDown, Smartphone, Monitor, Copy, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE as API } from '../../config';

export default function DevEmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [params, setParams] = useState({});
  const [html, setHtml] = useState('');
  const [subject, setSubject] = useState('');
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('desktop');
  const [copied, setCopied] = useState(false);
  const [sendTo, setSendTo] = useState('');
  const [sending, setSending] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/dev/email-templates`, { withCredentials: true })
      .then(r => {
        setTemplates(r.data);
        if (r.data.length > 0) {
          selectTemplate(r.data[0]);
        }
      })
      .catch(() => toast.error('Failed to load email templates'));
  }, []);

  const selectTemplate = (tpl) => {
    setActiveId(tpl.id);
    const defaults = {};
    tpl.params.forEach(p => { defaults[p.key] = p.default; });
    setParams(defaults);
    fetchPreview(tpl.id, defaults);
  };

  const fetchPreview = async (templateId, p) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/dev/email-templates/preview`, {
        template_id: templateId, params: p
      }, { withCredentials: true });
      setHtml(res.data.html);
      setSubject(res.data.subject);
    } catch {
      toast.error('Failed to render template');
    } finally {
      setLoading(false);
    }
  };

  const handleParamChange = (key, value) => {
    const next = { ...params, [key]: value };
    setParams(next);
    if (activeId) fetchPreview(activeId, next);
  };

  const handleCopyHtml = () => {
    navigator.clipboard.writeText(html).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('HTML copied to clipboard');
    });
  };

  const handleSendTest = async () => {
    if (!sendTo || !sendTo.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/dev/email-templates/send-test`, {
        to: sendTo, template_id: activeId, params
      }, { withCredentials: true });
      toast.success(`Test email sent to ${sendTo}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSending(false);
    }
  };

  const activeTpl = templates.find(t => t.id === activeId);

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="dev-email-templates">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-violet-500/10 rounded-lg">
          <Mail className="w-5 h-5 text-violet-400" />
        </div>
        <div>
          <h1 className="text-lg font-bold text-white" data-testid="email-templates-title">Email Templates</h1>
          <p className="text-xs text-slate-400">Preview and test transactional email templates</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
        {/* Left: Template List + Params */}
        <div className="space-y-4">
          {/* Template Selector */}
          <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden" data-testid="template-selector">
            <div className="px-3 py-2 border-b border-slate-800">
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Templates</span>
            </div>
            <div className="p-1.5 space-y-0.5">
              {templates.map(tpl => (
                <button
                  key={tpl.id}
                  onClick={() => selectTemplate(tpl)}
                  className={`w-full text-left px-3 py-2.5 rounded-md text-sm transition-colors ${
                    activeId === tpl.id
                      ? 'bg-violet-500/15 text-violet-300 font-medium'
                      : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                  }`}
                  data-testid={`template-btn-${tpl.id}`}
                >
                  <span className="block truncate">{tpl.name}</span>
                  <span className="block text-[10px] text-slate-500 mt-0.5 truncate">{tpl.description}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Parameter Editor */}
          {activeTpl && (
            <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden" data-testid="template-params">
              <div className="px-3 py-2 border-b border-slate-800">
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Parameters</span>
              </div>
              <div className="p-3 space-y-3">
                {activeTpl.params.map(p => (
                  <div key={p.key}>
                    <Label className="text-[11px] text-slate-400 mb-1 block">{p.label}</Label>
                    {p.options ? (
                      <select
                        value={params[p.key] || p.default}
                        onChange={e => handleParamChange(p.key, e.target.value)}
                        className="w-full bg-slate-800 border border-slate-700 rounded-md text-sm text-slate-200 px-2.5 py-1.5 focus:ring-1 focus:ring-violet-500 outline-none"
                        data-testid={`param-${p.key}`}
                      >
                        {p.options.map(opt => (
                          <option key={opt} value={opt}>{opt.replace('_', ' ')}</option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        value={params[p.key] || ''}
                        onChange={e => handleParamChange(p.key, e.target.value)}
                        className="bg-slate-800 border-slate-700 text-slate-200 text-sm h-8"
                        type={p.type === 'number' ? 'number' : 'text'}
                        data-testid={`param-${p.key}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Send Test */}
          <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden" data-testid="send-test-section">
            <div className="px-3 py-2 border-b border-slate-800">
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Send Test Email</span>
            </div>
            <div className="p-3 space-y-2">
              <Input
                type="email"
                placeholder="recipient@example.com"
                value={sendTo}
                onChange={e => setSendTo(e.target.value)}
                className="bg-slate-800 border-slate-700 text-slate-200 text-sm h-8"
                data-testid="send-test-email-input"
              />
              <Button
                onClick={handleSendTest}
                disabled={sending || !html}
                className="w-full bg-violet-600 hover:bg-violet-700 text-white text-xs h-8"
                data-testid="send-test-email-btn"
              >
                <Send className="w-3 h-3 mr-1.5" />
                {sending ? 'Sending...' : 'Send Test'}
              </Button>
            </div>
          </div>
        </div>

        {/* Right: Preview */}
        <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden flex flex-col" data-testid="template-preview">
          {/* Preview toolbar */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-800 bg-slate-900/80">
            <div className="flex items-center gap-3">
              <Eye className="w-4 h-4 text-slate-500" />
              <div>
                <span className="text-xs font-medium text-slate-300 block">Preview</span>
                {subject && <span className="text-[10px] text-slate-500 block">Subject: {subject}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Viewport toggle */}
              <div className="flex bg-slate-800 rounded-md p-0.5">
                <button
                  onClick={() => setViewMode('desktop')}
                  className={`p-1.5 rounded transition-colors ${viewMode === 'desktop' ? 'bg-slate-700 text-white' : 'text-slate-500 hover:text-slate-300'}`}
                  title="Desktop view"
                  data-testid="view-desktop-btn"
                >
                  <Monitor className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setViewMode('mobile')}
                  className={`p-1.5 rounded transition-colors ${viewMode === 'mobile' ? 'bg-slate-700 text-white' : 'text-slate-500 hover:text-slate-300'}`}
                  title="Mobile view"
                  data-testid="view-mobile-btn"
                >
                  <Smartphone className="w-3.5 h-3.5" />
                </button>
              </div>
              {/* Copy HTML */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyHtml}
                className="text-slate-400 hover:text-white text-xs h-7 px-2"
                disabled={!html}
                data-testid="copy-html-btn"
              >
                {copied ? <Check className="w-3 h-3 mr-1 text-emerald-400" /> : <Copy className="w-3 h-3 mr-1" />}
                {copied ? 'Copied' : 'Copy HTML'}
              </Button>
            </div>
          </div>

          {/* Email preview */}
          <div className={`flex-1 flex justify-center p-6 bg-slate-950/50 min-h-[500px] ${loading ? 'opacity-50' : ''}`}>
            <div
              className={`bg-white rounded-lg shadow-xl overflow-hidden transition-all duration-300 ${
                viewMode === 'mobile' ? 'w-[375px]' : 'w-full max-w-[600px]'
              }`}
              style={{ height: 'fit-content' }}
            >
              {/* Simulated email client header */}
              <div className="bg-gray-100 border-b px-4 py-3">
                <div className="flex items-center gap-1.5 mb-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                </div>
                <p className="text-[11px] text-gray-500">
                  <span className="font-medium text-gray-700">From:</span> {process.env.REACT_APP_SENDER_EMAIL || 'noreply@allaboutclaims.com'}
                </p>
                <p className="text-[11px] text-gray-500">
                  <span className="font-medium text-gray-700">Subject:</span> {subject || '(no subject)'}
                </p>
              </div>
              {/* Email body */}
              {html ? (
                <div
                  className="email-preview-body"
                  dangerouslySetInnerHTML={{ __html: html }}
                  data-testid="email-preview-body"
                />
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
                  Select a template to preview
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
